import React, { useState, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import {
  TriggerButton,
  TriggerLogoWrap,
  TriggerLogoIcon,
  TriggerLabelText,
  ModalTourTriggerWrapper,
  wshColors,
  wobble,
  buttonPulse,
  textFadeIn,
} from './wshComponents'

const TIPS = ['WholesaleHub Helper!', 'Need Help? Click Me!']
const SPIN_INTERVAL_MS = 8000
const EXPAND_DURATION_MS = 3500

// ─── Modal-sized variants ─────────────────────────────────────────────────────

const ModalTriggerButton = styled.button<{ $expanded: boolean }>`
  height: 32px;
  width: ${p => p.$expanded ? '160px' : '32px'};
  border-radius: 999px;
  border: none;
  background: ${wshColors.primary};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 51, 102, 0.25);
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1), background 0.15s;
  flex-shrink: 0;
  padding: 0;
  overflow: hidden;
  animation: ${css`${buttonPulse} 2.5s ease-in-out infinite`};

  &:hover {
    background: ${wshColors.primaryLight};
    width: 160px;
    animation: none;
  }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid ${wshColors.accent}; }
`

const ModalLogoWrap = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const ModalLogoIcon = styled.img<{ $animating: boolean }>`
  width: 20px;
  height: auto;
  filter: brightness(1.3);
  pointer-events: none;
  animation: ${p => p.$animating ? css`${wobble} 0.7s ease forwards` : 'none'};
`

const ModalLabelText = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  white-space: nowrap;
  padding-right: 0.75rem;
  letter-spacing: 0.01em;
  animation: ${css`${textFadeIn} 0.3s ease 0.2s both`};
`

// ─── Component ────────────────────────────────────────────────────────────────

interface WshTourButtonProps {
  onClick: () => void
  variant?: 'full' | 'modal'
}

const WshTourButton: React.FC<WshTourButtonProps> = ({ onClick, variant = 'full' }) => {
  const [tipIndex, setTipIndex] = useState(0)
  const [animating, setAnimating] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [hovered, setHovered] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const collapseRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const tips = TIPS

  const handleSpinEnd = () => {
    setAnimating(false)
    setTipIndex(i => (i + 1) % tips.length)
    setExpanded(true)
    if (collapseRef.current) clearTimeout(collapseRef.current)
    collapseRef.current = setTimeout(() => setExpanded(false), EXPAND_DURATION_MS)
  }

  useEffect(() => {
    intervalRef.current = setInterval(() => setAnimating(true), SPIN_INTERVAL_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (collapseRef.current) clearTimeout(collapseRef.current)
    }
  }, [])

  if (variant === 'modal') {
    return (
      <ModalTourTriggerWrapper>
        <ModalTriggerButton
          type="button"
          $expanded={expanded || hovered}
          onClick={onClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          title="WholesaleHub Helper"
          aria-label="Open help guide"
        >
          <ModalLogoWrap>
            <ModalLogoIcon
              src="/wsh/wsh_logo_grn.png"
              alt="WSH Help"
              $animating={animating}
              onAnimationEnd={handleSpinEnd}
            />
          </ModalLogoWrap>
          {(expanded || hovered) && (
            <ModalLabelText key={hovered ? 'hover' : tipIndex}>
              {hovered ? 'Open Guide →' : tips[tipIndex]}
            </ModalLabelText>
          )}
        </ModalTriggerButton>
      </ModalTourTriggerWrapper>
    )
  }

  return (
    <TriggerButton
      $expanded={expanded || hovered}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title="WholesaleHub Helper"
      aria-label="Open WSH help guide"
    >
      <TriggerLogoWrap>
        <TriggerLogoIcon
          src="/wsh/wsh_logo_grn.png"
          alt="WSH Help"
          $animating={animating}
          onAnimationEnd={handleSpinEnd}
        />
      </TriggerLogoWrap>
      {(expanded || hovered) && (
        <TriggerLabelText key={hovered ? 'hover' : tipIndex}>
          {hovered ? 'Open Help Guide →' : tips[tipIndex]}
        </TriggerLabelText>
      )}
    </TriggerButton>
  )
}

export default WshTourButton
